import { useCallback, useEffect, useMemo, useState } from "react";

import { SignalTestResultState, SignalTestRowState } from "../types";

import { SIGNAL_SAMPLE_REQUEST_LIMIT } from "./constants";
import { mapRequestToSignalSampleRequestLog } from "./utils";

import { useAuth } from "@/context/auth-context";
import { useRequestLogHydration } from "@/queries/requests/requestLogHydration";
import { SignalSampleRequestLog, useTestSignal } from "@/queries/signals";

interface UseSignalTestDrawerParams {
  workspaceId: number;
  prompt: string;
}

export const useSignalTestDrawer = ({ workspaceId, prompt }: UseSignalTestDrawerParams) => {
  const userToken = useAuth()?.userToken ?? "";
  const testSignalMutation = useTestSignal();
  const [selectedRequestLogIds, setSelectedRequestLogIds] = useState<number[]>([]);
  const [hydratedLogsById, setHydratedLogsById] = useState<Record<number, SignalSampleRequestLog>>(
    {},
  );
  const [testStates, setTestStates] = useState<Record<number, SignalTestRowState>>({});
  const [isTestingAll, setIsTestingAll] = useState(false);

  const uncachedRequestLogIds = useMemo(
    () => selectedRequestLogIds.filter(id => !(id in hydratedLogsById)),
    [hydratedLogsById, selectedRequestLogIds],
  );

  const { data: hydrationData, isFetching } = useRequestLogHydration({
    userToken,
    workspaceId,
    requestLogIds: uncachedRequestLogIds,
    enabled: Boolean(userToken) && uncachedRequestLogIds.length > 0,
  });

  useEffect(() => {
    if (!hydrationData?.items?.length) {
      return;
    }

    setHydratedLogsById(current => {
      const next = { ...current };
      for (const item of hydrationData.items) {
        next[Number(item.id)] = mapRequestToSignalSampleRequestLog(item);
      }
      return next;
    });
  }, [hydrationData?.items]);

  const requestLogs = useMemo(
    (): SignalSampleRequestLog[] =>
      selectedRequestLogIds.map(id => {
        const cached = hydratedLogsById[id];
        if (cached) {
          return cached;
        }

        return {
          id,
          prompt_id: null,
          prompt_version_number: null,
          prompt_name: null,
          tags_array: null,
          prompt_preview: null,
          request_response_preview: null,
          request_start_time: null,
        };
      }),
    [hydratedLogsById, selectedRequestLogIds],
  );

  const hasCachedRequestLogs = selectedRequestLogIds.some(id => id in hydratedLogsById);
  const isLoading = selectedRequestLogIds.length > 0 && !hasCachedRequestLogs && isFetching;

  const hasAssertion = prompt.trim().length > 0;

  const testStats = useMemo(() => {
    let running = 0;
    let trueCount = 0;
    let falseCount = 0;

    for (const state of Object.values(testStates)) {
      if (state.result === SignalTestResultState.LOADING) {
        running += 1;
      } else if (state.result === SignalTestResultState.TRUE) {
        trueCount += 1;
      } else if (state.result === SignalTestResultState.FALSE) {
        falseCount += 1;
      }
    }

    return { running, trueCount, falseCount };
  }, [testStates]);

  const hasTestActivity = Object.keys(testStates).length > 0;

  useEffect(() => {
    setTestStates({});
  }, [prompt]);

  const addRequestLogs = useCallback((ids: number[]): boolean => {
    const uniqueIncoming = Array.from(new Set(ids.filter(id => Number.isFinite(id))));
    if (uniqueIncoming.length === 0) {
      return false;
    }

    let didAdd = false;
    setSelectedRequestLogIds(current => {
      const existing = new Set(current);
      const next = [...current];

      for (const id of uniqueIncoming) {
        if (existing.has(id) || next.length >= SIGNAL_SAMPLE_REQUEST_LIMIT) {
          continue;
        }

        next.push(id);
        existing.add(id);
        didAdd = true;
      }

      return next.length === current.length ? current : next;
    });

    return didAdd;
  }, []);

  const removeRequestLog = useCallback((requestLogId: number) => {
    setSelectedRequestLogIds(current => current.filter(id => id !== requestLogId));
    setTestStates(current => {
      if (!(requestLogId in current)) {
        return current;
      }

      const next = { ...current };
      delete next[requestLogId];
      return next;
    });
  }, []);

  const handleTestRequest = async (requestLogId: number) => {
    if (!hasAssertion) {
      return;
    }

    setTestStates(current => ({
      ...current,
      [requestLogId]: {
        requestLogId,
        result: SignalTestResultState.LOADING,
      },
    }));

    try {
      const response = await testSignalMutation.mutateAsync({
        workspaceId,
        userToken,
        prompt,
        request_log_id: requestLogId,
      });

      setTestStates(current => ({
        ...current,
        [requestLogId]: {
          requestLogId,
          result:
            response.result?.label === "true"
              ? SignalTestResultState.TRUE
              : SignalTestResultState.FALSE,
          details: response.result,
        },
      }));
    } catch (error) {
      setTestStates(current => ({
        ...current,
        [requestLogId]: {
          requestLogId,
          result: SignalTestResultState.ERROR,
          errorMessage: error instanceof Error ? error.message : "Test failed",
        },
      }));
    }
  };

  const handleTestAll = async () => {
    if (!hasAssertion || requestLogs.length === 0) {
      return;
    }

    setIsTestingAll(true);
    try {
      await Promise.all(requestLogs.map(requestLog => handleTestRequest(requestLog.id)));
    } finally {
      setIsTestingAll(false);
    }
  };

  return {
    selectedRequestLogIds,
    requestLogs,
    hasAssertion,
    isLoading,
    testStates,
    isTestingAll,
    testStats,
    hasTestActivity,
    isTestPending: testSignalMutation.isPending,
    addRequestLogs,
    removeRequestLog,
    handleTestRequest,
    handleTestAll,
  };
};
